'use client';

import type { ProviderResponse } from '@/app/models/providersResponse';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import formatterPercentualValue from '@/app/utils/percentualUtils';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import DataTable, { getBodyCellSx } from '@/components/common/DataTable';
import { TableCell, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Column } from '../common/ExpandableDataTable';

type EventTableParams = {
  header?: ProviderResponse;
};

export default function ProviderTable(props: EventTableParams) {
  const theme = useTheme();
  const { header } = props;
  const { t } = useTranslation(['provider', 'common']);

  const columns: Column[] = [
    { key: 'resourceDonor', label: t('resource.donor'), width: theme.spacing(46) },
    { key: 'allocationProject', label: t('allocation.project'), width: theme.spacing(46) },
    { key: 'total', label: `${t('total')}` },
    { key: 'available', label: `${t('available')}` },
    { key: 'balance', label: `${t('balance')}` },
    { key: 'extra', label: `${t('extra')}` },
    { key: 'priority', label: t('priority'), align: 'center' },
    { key: 'eligible', label: `${t('eligible')}`, align: 'center' },
    { key: 'maxAllocation', label: `${t('max.allocation')}`, align: 'center' },
  ];

  const renderRow = (row: ProviderResponse) => {
    const cellSx = getBodyCellSx(true);

    return (
      <>
        <TableCell sx={cellSx} width={theme.spacing(46)}>
          <TruncatedTextWithTooltip value={`${row.provider.code} - ${row.provider.name}`} size={37} sx={cellSx} />
        </TableCell>
        <TableCell sx={cellSx} width={theme.spacing(46)}>
          <TruncatedTextWithTooltip value={`${row.toAllocate.code} - ${row.toAllocate.name}`} size={37} sx={cellSx} />
        </TableCell>
        <TableCell sx={cellSx}>{`${formatterMonetaryValue(row.totalValue)}`}</TableCell>
        <TableCell sx={cellSx}>{`${formatterMonetaryValue(row.availableValue)}`}</TableCell>
        <TableCell sx={cellSx}>{`${formatterMonetaryValue(row.balanceValue)}`}</TableCell>
        <TableCell sx={cellSx}>{`${formatterMonetaryValue(row.extraValue)}`}</TableCell>
        <TableCell sx={cellSx} align="center">{`${row.priority}`}</TableCell>
        <TableCell sx={cellSx} align="center">{`${formatterPercentualValue(row.eligiblePercent)}`}</TableCell>
        <TableCell sx={cellSx} align="center">{`${formatterPercentualValue(row.maxAllocation)}`}</TableCell>
      </>
    );
  };

  // Use provider ID as the unique key
  const getRowKey = (row: ProviderResponse) => row.provider.id;

  // Convert single header to array format for DataTable
  const data = header ? [header] : [];

  return (
    <DataTable
      columns={columns}
      data={data}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage=""
      stickyHeader={false}
      size="small"
      invertHeaderColors // Use the new prop to swap header and row colors
    />
  );
}
